<template>
  <div class="winLoseContainer" :style="containerConditionalStyle">
    <div class="header">
      <p class="title">{{ myName }}<span class="title">,</span></p>
      <img class="close" src="/img/icons/bone_close.svg" alt="close" @click="getOut" />
    </div>
    <div class="body">
      <p class="result" :class="isMeWon ? 'win' : 'lost'">{{ $t(isMeWon ? 'flipCoinYouWin' : 'flipCoinYouLost') }}!</p>
      <video
        v-if="isMeWon"
        class="sack"
        :style="{
          height: '100%',
          width: '100%',
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: -1
        }"
        preload="auto"
        loop
        muted
        playsinline
        autoplay
        :src="`/flipCoinSacks/${getTokenName(props.gameData?.tokenAddress || '')}.mp4`"
      ></video>

      <div
        :style="{
          marginTop: isMeWon ? '130px' : 0
        }"
        class="youGet"
      >
        {{ $t('buypackYouGet') }}:
        <img
          class="icon-image"
          :src="
            tokens[
              tokenMetaData(blockchain.contracts, props.gameData?.tokenAddress || '').name.contract as Tokens
            ].toString()
          "
          width="32"
          loading="lazy"
          alt="Token icon"
        />
        <span>
          {{
            isMeWon
              ? BigNumber(props.gameData?.winAmount || 0).toFormat(0, {
                  groupSize: 3,
                  groupSeparator: ' ',
                  decimalSeparator: '.'
                })
              : 0
          }}
        </span>
        <img
          v-if="currentPlayer?.seasonPoints"
          class="icon-image"
          :src="tokens.seasonPoints.toString()"
          width="32"
          loading="lazy"
          alt="Token icon"
        />
        <span v-if="currentPlayer?.seasonPoints">
          {{
            BigNumber(currentPlayer?.seasonPoints || 0).toFormat(0, {
              groupSize: 3,
              groupSeparator: ' ',
              decimalSeparator: '.'
            })
          }}
        </span>
      </div>

      <div class="buttons">
        <button class="btn-outline" @click="goToStats">
          {{ $t('flipCoinViewStats') }}
        </button>
        <TavernFlipCoinBigButton class="getOutBtn" :label="$t('flipCoinGetOut')" @click="getOut" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FlipCoinGame } from '~/types/FlipCoin';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import tokens from '~/public/img/tokens';
import { apiUrls, type Tokens } from '~/utils/constants';
import BigNumber from 'bignumber.js';
const emit = defineEmits(['go-to-stats', 'get-out']);
const { blockchain, apiUrl } = useEnvs();
const props = defineProps<{
  gameData: FlipCoinGame;
}>();

const { data: gameData } = await useFetch<FlipCoinGame>(() => apiUrls.flipCoinGame.game(props.gameData.gameId), {
  baseURL: apiUrl,
  server: false
});

const isMeWon = computed(() => {
  if (!props.gameData || !address.value) return false;

  const { winner } = props.gameData;
  return winner.toLowerCase() === address.value.toLowerCase();
});

const currentPlayer = computed(() => (!gameData.value ? null : getUserData(gameData.value, isMeWon.value)));

const { address } = useWeb3ModalAccount();

const getTokenName = (tokenAddress: string) => {
  return tokenMetaData(blockchain.contracts, tokenAddress).name.contract;
};

const myName = computed(() => {
  if (!props.gameData || !address.value) return false;

  const { ownerPlayer, joinerPlayer } = props.gameData;

  const player = address.value.toLowerCase() === ownerPlayer.userAddress.toLowerCase() ? ownerPlayer : joinerPlayer;

  return player?.userAddress?.toLowerCase().includes(player.userName.toLowerCase())
    ? formatAddress(player.userAddress)
    : player.userName;
});

const goToStats = () => {
  emit('go-to-stats');
};

const getOut = () => {
  emit('get-out');
};

const containerConditionalStyle = computed(() => ({
  background: isMeWon.value ? 'transparent' : 'linear-gradient(to bottom, #4e3127, #000000)'
}));
</script>

<style lang="scss" scoped>
.winLoseContainer {
  width: 375px;
  min-height: 207px;
  max-height: 382px;
  border-radius: 12px;
  margin: 0 auto;
  padding-top: 7px;
  border: 1px solid #483b20;
  position: relative;
  overflow: hidden;
}

.header {
  margin: 10px 20px;
  position: relative;

  .title {
    font-size: 24px;
    line-height: 24px;
    color: #fff;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    text-align: center;
  }

  .close {
    width: 20px;
    height: 20px;
    position: absolute;
    right: 0;
    top: 0;
    cursor: pointer;
  }
}

.sack {
  width: 121px;
  height: 121px;
}

.youGet {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  & > span {
    font-weight: 700;
    font-size: 24px;
  }

  img {
    margin: 0px 3px;
  }
}

.win {
  color: #12f83a;
}

.lost {
  color: #a2a2a2;
}

.body {
  text-align: center;
  font-size: 20px;
}

.result {
  font-weight: 600;
  font-size: 24px;
}

.buttons {
  display: flex;
  justify-content: center;
  margin-top: 12px;
  margin-bottom: 12px;
  gap: 10px;
  width: 100%;
  padding: 0 30px;

  button {
    flex-basis: 0.5;
    height: 52px;
    font-size: 18px;
    max-width: 100%;
    width: 100%;
    white-space: nowrap;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .getOutBtn {
    margin: 0px;
    line-height: 24px;
    padding: 0 24px;
  }
}
</style>
